import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, FileText, CheckCircle2, Upload, Sparkles, Copy, Wand2, Linkedin, ClipboardCheck } from "lucide-react";
import { track, EVENTS } from "@/lib/analytics";

const TABS = [
  { key: "upload", label: "Upload & Parse", icon: Upload },
  { key: "ats", label: "ATS Score", icon: ClipboardCheck },
  { key: "rewrite", label: "AI Rewrite", icon: Wand2 },
  { key: "cover", label: "Cover Letter", icon: FileText },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin },
];

export default function Resume() {
  const [tab, setTab] = useState("upload");
  const [profile, setProfile] = useState(null);

  useEffect(() => { api.get("/profile").then(r => setProfile(r.data)); }, []);

  const hasResume = !!profile?.resume_filename;

  return (
    <div className="p-10 max-w-6xl" data-testid="resume-page">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Module 01</div>
        <h1 className="font-display font-bold text-5xl tracking-tight">Resume AI</h1>
        <p className="text-muted-foreground mt-2 text-sm">Parse. Score. Rewrite. Personalize. Optimize.</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-px bg-border border border-border mb-8" data-testid="resume-tabs">
        {TABS.map(t => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={()=>setTab(t.key)}
              className={`flex-1 min-w-[140px] px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${active ? "bg-primary text-foreground" : "bg-card text-muted-foreground hover:text-foreground hover:bg-accent"}`}
            >
              <t.icon size={14}/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "upload" && <UploadTab profile={profile} onUpdate={setProfile}/>}
      {tab === "ats" && <AtsTab hasResume={hasResume}/>}
      {tab === "rewrite" && <RewriteTab hasResume={hasResume}/>}
      {tab === "cover" && <CoverTab hasResume={hasResume}/>}
      {tab === "linkedin" && <LinkedInTab/>}
    </div>
  );
}

function UploadTab({ profile, onUpdate }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [parsed, setParsed] = useState(null);

  const onDrop = (e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); };
  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/resume/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setParsed(data.parsed);
      onUpdate(data.profile);
      track(EVENTS.RESUME_UPLOADED, {
        file_type: file.name.split(".").pop()?.toLowerCase() || "unknown",
        file_size_bucket: file.size < 500000 ? "sm" : file.size < 2000000 ? "md" : "lg",
      });
      toast.success("Resume parsed and profile updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); } finally { setUploading(false); }
  };
  return (
    <>
      <div onDrop={onDrop} onDragOver={(e)=>e.preventDefault()} data-testid="resume-dropzone" className="border border-dashed border-border p-16 text-center hover:border-primary transition-colors">
        <UploadCloud size={48} className="mx-auto text-muted-foreground/60 mb-4" strokeWidth={1.25}/>
        <div className="font-display font-bold text-xl tracking-tight mb-2">Drop your resume here</div>
        <div className="text-sm text-muted-foreground mb-6">PDF or DOCX · up to 5MB</div>
        <label className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-6 py-2.5 text-sm cursor-pointer inline-block">
          Browse files
          <input data-testid="resume-file-input" type="file" accept=".pdf,.docx" onChange={e=>{const f=e.target.files?.[0]; if(f) setFile(f);}} className="hidden"/>
        </label>
        {file && (
          <div className="mt-6 flex items-center justify-center gap-3 text-sm">
            <FileText size={16}/> <span data-testid="resume-filename">{file.name}</span>
            <button data-testid="resume-upload-btn" onClick={upload} disabled={uploading} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-4 py-1.5 text-xs">{uploading ? "Parsing..." : "Parse resume"}</button>
          </div>
        )}
      </div>
      {profile?.resume_filename && <div className="mt-6 flex items-center gap-2 text-sm text-emerald-400 font-mono" data-testid="resume-current"><CheckCircle2 size={16}/> Current resume: {profile.resume_filename}</div>}
      {parsed && (
        <div className="mt-8 card-flat p-6" data-testid="resume-parsed">
          <div className="font-display font-bold text-xl tracking-tight mb-4">Parsed data</div>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <Stat label="Name" value={parsed.name}/>
            <Stat label="Role" value={parsed.current_role}/>
            <Stat label="Location" value={parsed.location}/>
            <Stat label="Years exp" value={parsed.years_experience}/>
            <Stat label="Experience entries" value={parsed.experience?.length || 0}/>
            <Stat label="Skills found" value={parsed.skills?.length || 0}/>
          </div>
        </div>
      )}
    </>
  );
}

function AtsTab({ hasResume }) {
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/resume/ats-score").then(r => setData(r.data)).catch(() => {}); }, []);
  const run = async () => {
    setLoading(true);
    try { const { data } = await api.post("/resume/ats-score", { target_role: role }); setData(data); toast.success("ATS report ready"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };
  if (!hasResume) return <NoResume/>;
  return (
    <div data-testid="ats-tab">
      <div className="flex gap-2 mb-6">
        <input data-testid="ats-role" value={role} onChange={e=>setRole(e.target.value)} placeholder="Target role (optional, e.g., Senior PM)" className="flex-1 bg-card border border-border px-4 py-3"/>
        <button data-testid="ats-run" onClick={run} disabled={loading} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-6">{loading? "Scoring..." : data ? "Rescore" : "Run ATS Score"}</button>
      </div>
      {data && (
        <div className="space-y-6" data-testid="ats-result">
          <div className="card-flat p-8 flex items-center gap-8">
            <ScoreCircle score={data.overall_score}/>
            <div className="flex-1">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Verdict</div>
              <div className="text-base text-zinc-200 leading-relaxed">{data.verdict}</div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-border border border-border">
            {Object.entries(data.sub_scores || {}).map(([k,v]) => (
              <div key={k} className="bg-background p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{k.replace(/_/g,' ')}</div>
                <div className="font-display font-bold text-2xl">{v}<span className="text-sm text-muted-foreground font-mono">/100</span></div>
                <div className="h-1 bg-secondary mt-2"><div className="h-full bg-primary" style={{ width: `${v}%` }}/></div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Panel title="Strengths" items={data.strengths} tone="good"/>
            <Panel title="Weaknesses" items={data.weaknesses} tone="bad"/>
          </div>
          <Panel title="Missing keywords" items={data.missing_keywords} tone="neutral" chips/>
          <Panel title="Action items" items={data.action_items} tone="neutral"/>
        </div>
      )}
    </div>
  );
}

function RewriteTab({ hasResume }) {
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const run = async () => {
    setLoading(true);
    try { const { data } = await api.post("/resume/rewrite", { target_role: role }); setData(data); toast.success("Resume rewritten"); }
    catch (e) { toast.error("Failed"); } finally { setLoading(false); }
  };
  if (!hasResume) return <NoResume/>;
  return (
    <div data-testid="rewrite-tab">
      <div className="flex gap-2 mb-6">
        <input data-testid="rewrite-role" value={role} onChange={e=>setRole(e.target.value)} placeholder="Target role (optional)" className="flex-1 bg-card border border-border px-4 py-3"/>
        <button data-testid="rewrite-run" onClick={run} disabled={loading} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-6">{loading? "Rewriting..." : "Rewrite"}</button>
      </div>
      {data && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="rewrite-result">
          <div className="card-flat p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Improved Resume</div>
              <button onClick={()=>{navigator.clipboard.writeText(data.improved_resume); toast.success("Copied");}} data-testid="rewrite-copy" className="text-xs text-primary hover:underline flex items-center gap-1"><Copy size={12}/> Copy</button>
            </div>
            <pre className="whitespace-pre-wrap text-sm text-zinc-200 font-body max-h-[600px] overflow-y-auto">{data.improved_resume}</pre>
          </div>
          <div className="card-flat p-4">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Key changes</div>
            <ul className="space-y-2">
              {(data.key_changes||[]).map((c,i)=> <li key={i} className="text-sm text-foreground/80 flex gap-2"><Sparkles size={12} className="text-primary mt-1 shrink-0"/> {c}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function CoverTab({ hasResume }) {
  const [jd, setJd] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const run = async () => {
    if (!jd.trim()) { toast.error("Paste a job description"); return; }
    setLoading(true);
    try { const { data } = await api.post("/resume/cover-letter", { job_description: jd, company }); setData(data); toast.success("Cover letter ready"); }
    catch (e) { toast.error("Failed"); } finally { setLoading(false); }
  };
  if (!hasResume) return <NoResume/>;
  return (
    <div data-testid="cover-tab">
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <input data-testid="cover-company" value={company} onChange={e=>setCompany(e.target.value)} placeholder="Company (optional)" className="bg-card border border-border px-4 py-3"/>
        <button data-testid="cover-run" onClick={run} disabled={loading} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors">{loading? "Writing..." : "Generate cover letter"}</button>
      </div>
      <textarea data-testid="cover-jd" value={jd} onChange={e=>setJd(e.target.value)} rows={6} placeholder="Paste the job description here..." className="w-full bg-card border border-border px-4 py-3 mb-6"/>
      {data && (
        <div className="card-flat p-6" data-testid="cover-result">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Cover Letter</div>
            <button onClick={()=>{navigator.clipboard.writeText(data.cover_letter); toast.success("Copied");}} className="text-xs text-primary hover:underline flex items-center gap-1"><Copy size={12}/> Copy</button>
          </div>
          <div className="whitespace-pre-wrap text-sm text-zinc-200 leading-relaxed">{data.cover_letter}</div>
        </div>
      )}
    </div>
  );
}

function LinkedInTab() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const run = async () => {
    setLoading(true);
    try { const { data } = await api.post("/resume/linkedin"); setData(data); toast.success("LinkedIn optimized"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };
  return (
    <div data-testid="linkedin-tab">
      <button data-testid="linkedin-run" onClick={run} disabled={loading} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors mb-6 px-6 py-2.5">{loading? "Optimizing..." : "Optimize my LinkedIn"}</button>
      {data && (
        <div className="space-y-4" data-testid="linkedin-result">
          <div className="card-flat p-6">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Headline</div>
            <div className="font-display font-bold text-xl">{data.headline}</div>
          </div>
          <div className="card-flat p-6">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">About</div>
            <div className="whitespace-pre-wrap text-sm text-zinc-200 leading-relaxed">{data.about}</div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Panel title="Featured skills" items={data.featured_skills} tone="neutral" chips/>
            <Panel title="Recruiter keywords" items={data.keywords} tone="neutral" chips/>
          </div>
          <Panel title="Posting ideas" items={data.posting_ideas} tone="neutral"/>
        </div>
      )}
    </div>
  );
}

function NoResume() {
  return <div className="card-flat p-10 text-center text-muted-foreground">Upload a resume first to use this tool.</div>;
}

function Stat({ label, value }) {
  return (
    <div className="border border-border p-3">
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1 font-display font-bold text-base">{value || "—"}</div>
    </div>
  );
}

function ScoreCircle({ score }) {
  const pct = Math.max(0, Math.min(100, score));
  const c = pct >= 80 ? "text-emerald-400" : pct >= 60 ? "text-amber-400" : "text-red-400";
  return (
    <div className="relative w-32 h-32">
      <svg viewBox="0 0 100 100" className="w-32 h-32">
        <circle cx="50" cy="50" r="45" stroke="#2a2a2a" strokeWidth="6" fill="none"/>
        <circle cx="50" cy="50" r="45" stroke="currentColor" strokeWidth="6" fill="none"
          className={c}
          strokeDasharray={`${(pct/100)*283} 283`} strokeLinecap="round" transform="rotate(-90 50 50)"/>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className={`font-display font-black text-3xl ${c}`}>{pct}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">ATS</div>
      </div>
    </div>
  );
}

function Panel({ title, items = [], tone = "neutral", chips = false }) {
  const dot = { good: "bg-emerald-400", bad: "bg-red-400", neutral: "bg-primary" }[tone];
  return (
    <div className="card-flat p-5">
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">{title}</div>
      {chips ? (
        <div className="flex flex-wrap gap-2">
          {items?.map((it,i)=> <span key={i} className="text-xs bg-card border border-border px-2 py-1">{it}</span>)}
        </div>
      ) : (
        <ul className="space-y-2">
          {items?.map((it,i)=> (
            <li key={i} className="text-sm text-foreground/80 flex gap-2">
              <span className={`w-1.5 h-1.5 mt-2 shrink-0 ${dot}`}/>{it}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
